class DateRangeValidation {
	constructor(config) {
		this.config = config;
		this.startDateObject = {};
		this.endDateObject = {};
		this.startDateValid = {valid: true};
		this.endDateValid = {valid: true};
	}

	get startDateInput() {
		return document.querySelector(`#${this.config.startDateId}`);
	}

	get endDateInput() {
		return document.querySelector(`#${this.config.endDateId}`);
	}

	get inputErrorClass() {
		return 'c-inputgroup__input--errored';
	}

	get validationTextClass() {
		return 'c-inputgroup__validation-text';
	}

	get validationTextErroredClass() {
		return 'c-inputgroup__validation-text--errored';
	}

	_getAssistiveText(dateInput) {
		return dateInput.nextElementSibling.innerHTML;
	}

	_resetHours(date) {
		return date.setHours(0, 0, 0, 0);
	}

	// for the cases the date input field is actually a text input
	_isDateInput(input) {
		return input.type === 'month' || input.type === 'date';
	}

	_inputHasMinMaxDate(dateInput) {
		const hasMinAttribute = dateInput.hasAttribute('min') || dateInput.hasAttribute('data-min');
		const hasMaxAttribute = dateInput.hasAttribute('max') || dateInput.hasAttribute('data-max');

		return hasMinAttribute && hasMaxAttribute;
	}

	_getMinDate(dateInput) {
		const isDataAttribute = dateInput.hasAttribute('min') ? '' : 'data-';
		return dateInput.getAttribute(`${isDataAttribute}min`);
	}

	_getMaxDate(dateInput) {
		const isDataAttribute = dateInput.hasAttribute('max') ? '' : 'data-';
		return dateInput.getAttribute(`${isDataAttribute}max`);
	}

	_populateDateObject() {
		this.startDateObject = {
			value: this.startDateInput.value.trim()
		};

		if (this._inputHasMinMaxDate(this.startDateInput)) {
			this.minStartDateFormatted = this._makeDate(this._getMinDate(this.startDateInput)).date;
			this.maxStartDateFormatted = this._makeDate(this._getMaxDate(this.startDateInput)).date;

			this.startDateObject.min = this._resetHours(this.minStartDateFormatted);
			this.startDateObject.max = this._resetHours(this.maxStartDateFormatted);
		}

		this.endDateObject = {
			value: this.endDateInput.value.trim()
		};

		if (this._inputHasMinMaxDate(this.endDateInput)) {
			this.minEndDateFormatted = this._makeDate(this._getMinDate(this.endDateInput)).date;
			this.maxEndDateFormatted = this._makeDate(this._getMaxDate(this.endDateInput)).date;

			this.endDateObject.min = this._resetHours(this.minEndDateFormatted);
			this.endDateObject.max = this._resetHours(this.maxEndDateFormatted);
		}
	}

	_setErrorMessages() {
		const dateFormatExample = (this.config.dateFormat === 'year-month-day') ? '2019-01-30' : '2019-01';

		this.errorMessagesStartDate = {
			dateEmptyField: 'Choose a start date.',
			dateDayWrongFormat: `Write the date as "${this.config.dateFormat}", for example "${dateFormatExample}".`,
			dateBetweenMinAndMax: `Choose a month between ${this._getMinDate(this.startDateInput)} and ${this._getMaxDate(this.startDateInput)}`,
			dateFromToComparison: 'Choose a start date that is on or before the end date.'
		};
		this.errorMessagesEndDate = {
			dateEmptyField: 'Choose an end date.',
			dateDayWrongFormat: `Write the date as "${this.config.dateFormat}", for example "${dateFormatExample}".`,
			dateBetweenMinAndMax: `Choose a month between ${this._getMinDate(this.endDateInput)} and ${this._getMaxDate(this.endDateInput)}`,
			dateFromToComparison: 'Choose an end date that is on or after the start date.'
		};
	}

	_getErrorMessage(errorMessage) {
		return `<svg  aria-hidden="true" width="16" height="16" class="c-inputgroup__validation-icon c-inputgroup__validation-icon--errored">
			<use href="/static/img/svg-sprite.svg#icon-error"></use>
		</svg>
		<span>${errorMessage}</span>`;
	}

	_makeDate(dateValue) {
		const temporary = dateValue.split('-');
		const year = Number.parseInt(temporary[0], 10);
		const month = Number.parseInt(temporary[1], 10); // January gives 0
		let date = new Date(year, month - 1);

		if (this.config.dateFormat === 'year-month-day') {
			const day = Number.parseInt(temporary[2], 10);
			date = new Date(year, month - 1, day);

			return {
				date,
				year,
				month,
				day
			};
		}

		return {
			date,
			year,
			month
		};
	}

	isDateValid(dateObject) {
		const dateValue = dateObject.value;
		// Capture the full year (\d{4}) and the full month value (\d{2}) from a string of the form 'YYYY-MM':
		let dateRegex = /^\d{4}-\d{2}$/;

		if (this.config.dateFormat === 'year-month-day') {
			// Capture the full year (\d{4}), the full month value (\d{2}) and the full date value (\d{2}) from a string of the form 'YYYY-MM-DD':
			dateRegex = /^\d{4}-\d{2}-\d{2}$/;
		}

		if (dateValue !== '') {
			// if the value has an invalid format OR is an invalid date
			if (!dateRegex.test(dateValue) || !this._isFormatValid(dateValue)) {
				return this._invalidDate('dateDayWrongFormat');
			}

			if (dateObject.min && dateObject.max && !this._isBetweenMinAndMax(dateObject)) {
				return this._invalidDate('dateBetweenMinAndMax');
			}

			if (!this._startDateEarlierThanEndDate(this.startDateObject.value, this.endDateObject.value)) {
				return this._invalidDate('dateFromToComparison');
			}

			return {valid: true};
		}

		return this._invalidDate('dateEmptyField');
	}

	_invalidDate(message) {
		return {
			valid: false,
			error: message
		};
	}

	_isFormatValid(dateValue) {
		const parsedDate = this._makeDate(dateValue);
		let result = parsedDate.date.getFullYear() === parsedDate.year &&
			parsedDate.date.getMonth() === parsedDate.month - 1;

		if (this.config.dateFormat === 'year-month-day') {
			result = result && parsedDate.date.getDate() === parsedDate.day;
		}
		return result;
	}

	_isBetweenMinAndMax(dateObject) {
		const date = this._resetHours(this._makeDate(dateObject.value).date);
		const result = date >= dateObject.min && date <= dateObject.max;

		return result;
	}

	_startDateEarlierThanEndDate(startDateValue, endDateValue) {
		return (this._resetHours(new Date(startDateValue)) <= this._resetHours(new Date(endDateValue)));
	}

	_handleErrorStateOnInput(elementChanged, state, error) {
		const parent = elementChanged.parentNode;

		if (state !== 'add') {
			elementChanged.classList.remove(this.inputErrorClass);
			if (parent.lastChild.classList && parent.lastChild.classList.contains(this.validationTextErroredClass)) {
				parent.lastChild.remove();
			}

			return;
		}

		if (elementChanged.classList.contains(this.inputErrorClass)) {
			parent.lastChild.innerHTML = this._getErrorMessage(error);
		} else {
			const fragment = document.createDocumentFragment();
			const messageElement = document.createElement('p');

			messageElement.className = `${this.validationTextClass} ${this.validationTextErroredClass}`;
			messageElement.innerHTML = this._getErrorMessage(error);
			fragment.append(messageElement);

			elementChanged.classList.add(this.inputErrorClass);
			parent.append(fragment);
		}
	}

	init() {
		const dateInputs = [this.startDateInput, this.endDateInput];

		if (dateInputs.every(input => input !== null && input !== undefined)) {
			const validateDates = () => {
				this._populateDateObject();
				this.startDateValid = this.isDateValid(this.startDateObject);

				if (this.startDateValid.valid) {
					this._handleErrorStateOnInput(this.startDateInput, 'remove');
				} else {
					this._handleErrorStateOnInput(this.startDateInput, 'add', this.errorMessagesStartDate[this.startDateValid.error]);
				}

				this.endDateValid = this.isDateValid(this.endDateObject);

				if (this.endDateValid.valid) {
					this._handleErrorStateOnInput(this.endDateInput, 'remove');
				} else {
					this._handleErrorStateOnInput(this.endDateInput, 'add', this.errorMessagesEndDate[this.endDateValid.error]);
				}
			};

			this._populateDateObject();
			this._setErrorMessages();

			for (const input of dateInputs) {
				input.addEventListener('change', event => validateDates(input));
			}
		}
	}
}

export default DateRangeValidation;
